import { useContext, useMemo } from 'react'

import { SanityComplexPortableText } from '@data/sanity/queries/types/content'
import { SanityPrintSlotType } from '@data/sanity/queries/types/print'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'

import Button from '@components/buttons/button'
import ComplexPortableText from '@components/portable-text/complex'
import ShopFormPrintItem from '../print-item'

interface ReviewStepReviewSectionProps {
  title: string
  description?: SanityComplexPortableText
  printSlotType: SanityPrintSlotType
}

const ReviewStepReviewSection = ({
  title,
  description,
  printSlotType,
}: ReviewStepReviewSectionProps) => {
  const { printSlots, shopState, setActiveStepIndex } =
    useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const prints = useMemo(() => {
    const prints = shopState.prints ?? []

    return prints.filter((print) => print.printSlotType === printSlotType)
  }, [printSlotType, shopState])

  const slotUploadStepIndex = useMemo(() => {
    const index = printSlots.findIndex(
      (printSlot) => printSlot.type === printSlotType,
    )

    return index > -1 ? index : 0
  }, [printSlots, printSlotType])

  return (
    <div className="px-5 sm:px-8 py-8 border border-gray-light bg-white space-y-8">
      <div className="space-y-5">
        <div className="space-y-3">
          <h3 className="leading-tight font-semibold text-[22px]">{title}</h3>
          {!!description && (
            <ComplexPortableText
              content={description}
              className="rc rc-text-base"
            />
          )}
        </div>

        <div className="space-y-4 sm:space-y-5">
          {prints.map((print) => (
            <ShopFormPrintItem key={print.id} mode="view" print={print} />
          ))}
        </div>

        {prints.length === 0 && (
          <p className="text-center text-base opacity-30">
            {shopFormStrings.shopFormPrintListEmpty}
          </p>
        )}
      </div>

      <Button
        variant="primary"
        color="red"
        size="normal-small"
        icon="ArrowRight"
        onClick={() => {
          setActiveStepIndex(slotUploadStepIndex)
        }}
      >
        {shopFormStrings.shopFormEditSlotButton}
      </Button>
    </div>
  )
}

export default ReviewStepReviewSection
